import { useTranslation } from "react-i18next";
import { Grid } from "semantic-ui-react";

import { StatusFilters } from "components";
import { useDealerAndLocationsGroupedOptions } from "components/DealersAndLocationsDropdown/hooks";
import { createFormHelper } from "lib/Form";
import { useAccountLandingPagesQuery } from "modules/AccountSettings/hooks";
import { SELECT_ALL_CAR_TYPES, useVehicleTypeDropdownOptions } from "modules/AccountSettings/hooks/useVehicleTypeDropdownOptions";

import type { DropdownItemProps, StatusFilter } from "components";
import type { User } from "models";

type SettingsSectionProps = {
  handleLocationDropdownChange: (value: number | undefined, options: DropdownItemProps<string>[]) => void;
  handleAppointmentStatusChange: (data: StatusFilter[]) => void;
  appointmentStatusIdentifiersSetting: StatusFilter[];
};

const FormHelper =
  createFormHelper<
    Pick<User, "dealer_location_id" | "car_type_filter" | "is_counter_tablet_user" | "is_vat_enabled_on_wo" | "landing_page_id" | "list_appointments_by_scheduled_range">
  >();

export const SettingsSection = ({ handleLocationDropdownChange, handleAppointmentStatusChange, appointmentStatusIdentifiersSetting }: SettingsSectionProps) => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();
  const [currentLocationId, selectedCarTypeFilter] = watch(["dealer_location_id", "car_type_filter"]);

  const { data: landingPageOptions = [], isLoading: isLandingPagesLoading } = useAccountLandingPagesQuery();
  const vehicleTypeOptions = useVehicleTypeDropdownOptions();
  const dealerAndLocationsGroupedOptions = useDealerAndLocationsGroupedOptions({ selectAll: false });

  const selectedVehicleTypeOption = vehicleTypeOptions.find(option => option.value === (selectedCarTypeFilter ?? SELECT_ALL_CAR_TYPES));

  const appointmentListOptions = [
    { key: "time_car_app", text: t("v8_appointment_date", "Appointment date"), value: false },
    { key: "scheduled_in_out", text: t("v8_scheduled_in_out", "Scheduled in/out date"), value: true }
  ];

  // TODO: once FormHelper.Field.Dropdown does not return the field type but the proper dropdown option.value type we can remove toString() here
  // TODO: search does not work properly for dealers, we need to have one component that we reuse to select a location instead of using useDealerAndLocationsGroupedOptions in multiple places like this
  return (
    <div className="setting_section">
      <h2>{t("v8_settings", "Settings")}</h2>
      <Grid columns={2}>
        <Grid.Column>
          <label className="input-label">{t("v8_location", "Location")}</label>
          <FormHelper.Field.Dropdown
            name="dealer_location_id"
            dropdownProps={{
              selection: true,
              floating: true,
              noResultsMessage: t("v8_no_locations", "No locations"),
              placeholder: t("v8_select_location", "Select a location"),
              fluid: true,
              scrolling: true,
              search: true,
              value: "location-" + currentLocationId
            }}
            options={dealerAndLocationsGroupedOptions}
            onChange={value => handleLocationDropdownChange(value, dealerAndLocationsGroupedOptions)}
            parseOptionValueToFieldValue={value => parseInt(value?.split("-").at(1) ?? "0")}
          />
        </Grid.Column>

        <Grid.Column>
          <label className="input-label">{t("v8_landing_page", "Landing page")}</label>
          <FormHelper.Field.Dropdown
            name="landing_page_id"
            options={landingPageOptions}
            dropdownProps={{ selection: true, fluid: true, loading: isLandingPagesLoading }}
          />
        </Grid.Column>
      </Grid>

      <Grid columns={2}>
        <Grid.Column>
          <label className="input-label">{t("v8_list_appointments_by", "List appointments by")}</label>
          <FormHelper.Field.Dropdown
            name="list_appointments_by_scheduled_range"
            options={appointmentListOptions}
            dropdownProps={{
              fluid: true,
              search: true,
              floating: true,
              selection: true,
              placeholder: t("v8_appointment_date", "Appointment date")
            }}
          />
        </Grid.Column>

        <Grid.Column>
          <label className="input-label">{t("v8_vehicle_type", "Vehicle type")}</label>
          <FormHelper.Field.Dropdown
            name="car_type_filter"
            options={vehicleTypeOptions}
            parseOptionValueToFieldValue={value => (value && value > SELECT_ALL_CAR_TYPES ? value : null)}
            dropdownProps={{
              fluid: true,
              placeholder: t("v8_select_vehicle_type", { defaultValue: "Select vehicle type" }),
              trigger: selectedVehicleTypeOption?.content
            }}
          />
        </Grid.Column>
      </Grid>

      <Grid>
        <Grid.Column width={9}>
          <label className="input-label">{t("v8_default_statuses", "Default statuses")}</label>
          <StatusFilters onChange={handleAppointmentStatusChange} filters={appointmentStatusIdentifiersSetting} />
        </Grid.Column>

        <Grid.Column width={3} className="mt-25">
          <FormHelper.Field.Checkbox checkboxProps={{ toggle: true }} name="is_counter_tablet_user" label={t("v8_use_counter_tablets", "Use counter tablets")} />
        </Grid.Column>
        <Grid.Column width={3} className="mt-25">
          <FormHelper.Field.Checkbox checkboxProps={{ toggle: true }} name="is_vat_enabled_on_wo" label={t("v8_show_price_with_vat", "Show price with VAT")} />
        </Grid.Column>
      </Grid>
    </div>
  );
};
